import { api } from './api';
import type { SavedDocument } from '../api/schemas';

/**
 * Řetězení dokladů: Nabídka → Objednávka → Soupis prací → Zjišťovací protokol
 * → Faktura (+ Předávací protokol díla jako příloha faktury).
 *
 * Do řetězce jde vstoupit u kteréhokoli dokladu a kdykoli skončit. Každý doklad
 * si v `data_json.zdroj` pamatuje, ze kterého vznikl (`ChainRef`). Přes `koren`
 * (id prvního dokladu řetězce) se pak dohledají sourozenci — předchozí ZP pro
 * „dosud provedeno" nebo předávací protokol jako příloha faktury.
 *
 * Bez nové tabulky a bez migrace — vše žije v `data_json` uložených dokumentů.
 */

export type ChainDocType =
  | 'nabidka'
  | 'objednavka'
  | 'soupis'
  | 'zjistovaci'
  | 'faktura'
  | 'predavaci-dilo';

export const CHAIN_LABEL: Record<ChainDocType, string> = {
  nabidka: 'Cenová nabídka',
  objednavka: 'Objednávka',
  soupis: 'Soupis prací',
  zjistovaci: 'Zjišťovací protokol',
  faktura: 'Faktura',
  'predavaci-dilo': 'Předávací protokol — dílo',
};

/** 2. pád do textů typu „dle zjišťovacího protokolu č. 1". */
const CHAIN_GENITIV: Record<ChainDocType, string> = {
  nabidka: 'cenové nabídky',
  objednavka: 'objednávky',
  soupis: 'soupisu prací',
  zjistovaci: 'zjišťovacího protokolu',
  faktura: 'faktury',
  'predavaci-dilo': 'předávacího protokolu',
};

/** Kam jde který doklad převést. */
export const CHAIN_TARGETS: Record<ChainDocType, ChainDocType[]> = {
  nabidka: ['objednavka', 'soupis', 'faktura'],
  objednavka: ['soupis', 'predavaci-dilo', 'faktura'],
  soupis: ['zjistovaci', 'faktura', 'predavaci-dilo'],
  zjistovaci: ['faktura'],
  'predavaci-dilo': ['faktura'],
  faktura: [],
};

export function isChainDocType(t: string): t is ChainDocType {
  return t in CHAIN_LABEL;
}

export function chainPath(t: string): string {
  return `/app/${t}`;
}

/** Odkaz na zdrojový doklad, uložený v `data_json.zdroj` nového dokladu. */
export interface ChainRef {
  id: number;
  doc_type: ChainDocType;
  title: string;
  /** Id prvního dokladu řetězce (nabídka/objednávka/…); u prvního = jeho id. */
  koren: number;
  /** Smluvní základ zakázky — nese se celým řetězcem (číslo + cena bez DPH). */
  smlouva?: { cislo: string; castka: string; typ?: 'objednavka' | 'sod' };
}

export function readChainRef(v: unknown): ChainRef | null {
  if (!v || typeof v !== 'object') return null;
  const r = v as Partial<ChainRef>;
  if (typeof r.id !== 'number' || typeof r.doc_type !== 'string' || !isChainDocType(r.doc_type)) {
    return null;
  }
  return {
    id: r.id,
    doc_type: r.doc_type,
    title: typeof r.title === 'string' ? r.title : CHAIN_LABEL[r.doc_type],
    koren: typeof r.koren === 'number' ? r.koren : r.id,
    smlouva: r.smlouva && typeof r.smlouva === 'object' ? r.smlouva : undefined,
  };
}

// ─── čísla ──────────────────────────────────────────────────────────────────

export function parseCzNum(s: unknown): number {
  if (s == null) return 0;
  const cleaned = String(s)
    .replace(/\s/g, '')
    .replace(/Kč/gi, '')
    .replace(',', '.')
    .replace(/[^\d.-]/g, '');
  const n = parseFloat(cleaned);
  return isFinite(n) ? n : 0;
}

/** Číslo do vstupního pole: bez oddělovačů tisíců, desetinná čárka, max 2 místa. */
export function fmtCzNum(n: number): string {
  if (!isFinite(n)) return '';
  return String(Math.round(n * 100) / 100).replace('.', ',');
}

// ─── společný tvar dat ──────────────────────────────────────────────────────

interface Firma {
  nazev: string;
  sidlo: string;
  ico: string;
  dic: string;
  tel: string;
  email: string;
  banka: string;
  ucet: string;
}

interface Polozka {
  /** `section` = mezinadpis (jen soupis). */
  section?: boolean;
  popis: string;
  mj: string;
  mnozstvi: string;
  cena: string;
}

/** Normalizovaný obsah dokladu — mezikrok mezi zdrojem a cílem převodu. */
interface Common {
  cislo: string;
  zakazka: string;
  zaObdobi: string;
  stavba: { nazev: string; adresa: string; cPovoleni: string; iniciály: string };
  objednatel: Firma;
  zhotovitel: Firma;
  polozky: Polozka[];
  dphPct: string;
  /** Celkem bez DPH. */
  celkem: number;
}

const emptyFirma = (): Firma => ({
  nazev: '', sidlo: '', ico: '', dic: '', tel: '', email: '', banka: '', ucet: '',
});

type Obj = Record<string, unknown>;
const o = (v: unknown): Obj => (v && typeof v === 'object' ? (v as Obj) : {});
const s = (v: unknown): string => (v == null ? '' : String(v));
const arr = (v: unknown): Obj[] => (Array.isArray(v) ? (v as Obj[]) : []);

function firma(src: Obj, map: Partial<Record<keyof Firma, string>> = {}): Firma {
  const f = emptyFirma();
  (Object.keys(f) as Array<keyof Firma>).forEach((k) => {
    f[k] = s(src[map[k] ?? k]);
  });
  return f;
}

function sumPolozky(p: Polozka[]): number {
  return p.reduce((a, x) => (x.section ? a : a + parseCzNum(x.mnozstvi) * parseCzNum(x.cena)), 0);
}

function toCommon(type: ChainDocType, d: Obj): Common {
  const st = o(d.stavba);
  const base: Common = {
    cislo: s(d.cislo),
    zakazka: s(d.zakazka),
    zaObdobi: '',
    stavba: {
      nazev: s(st.nazev),
      adresa: s(st.adresa),
      cPovoleni: s(st.cPovoleni),
      iniciály: s(st['iniciály']),
    },
    objednatel: firma(o(d.obj)),
    zhotovitel: emptyFirma(),
    polozky: [],
    dphPct: d.dphPct != null ? s(d.dphPct) : '21',
    celkem: 0,
  };

  switch (type) {
    case 'nabidka': {
      base.zhotovitel = firma(o(d.dod));
      base.polozky = arr(d.items).map((it) => ({
        popis: s(it.desc), mj: s(it.unit), mnozstvi: s(it.qty), cena: s(it.price),
      }));
      base.celkem = sumNabidkaFaktura(arr(d.items));
      break;
    }
    case 'objednavka': {
      base.zhotovitel = firma(o(d.dod));
      // Kontakty obou stran jsou v seznamech osob — první osoba dá telefon/e-mail.
      const ko = arr(d.kontaktyObj)[0] ?? {};
      const kd = arr(d.kontaktyDod)[0] ?? {};
      base.objednatel.tel = s(ko.tel);
      base.objednatel.email = s(ko.email);
      base.zhotovitel.tel = s(kd.tel);
      base.zhotovitel.email = s(kd.email);
      base.polozky = arr(d.polozky).map((it) => ({
        popis: s(it.popis), mj: s(it.jednotka), mnozstvi: s(it.mnozstvi), cena: s(it.cena),
      }));
      base.celkem = sumPolozky(base.polozky);
      break;
    }
    case 'soupis': {
      base.zhotovitel = firma(o(d.zhot));
      base.polozky = arr(d.rows).map((r) =>
        r.type === 'section'
          ? { section: true, popis: s(r.title), mj: '', mnozstvi: '', cena: '' }
          : { popis: s(r.title), mj: s(r.unit), mnozstvi: s(r.qty), cena: s(r.price) },
      );
      base.celkem = sumPolozky(base.polozky);
      break;
    }
    case 'zjistovaci': {
      base.zhotovitel = firma(o(d.zhot));
      base.zaObdobi = s(d.zaObdobi);
      base.celkem = parseCzNum(o(d.prace).curr);
      break;
    }
    case 'faktura': {
      const m = o(d.meta);
      base.cislo = s(m.cislo);
      base.zhotovitel = firma(o(d.dod));
      base.polozky = arr(d.items).map((it) => ({
        popis: s(it.desc), mj: s(it.unit), mnozstvi: s(it.qty), cena: s(it.price),
      }));
      base.celkem = sumNabidkaFaktura(arr(d.items));
      break;
    }
    case 'predavaci-dilo': {
      base.cislo = s(d.cSmlouvy);
      base.stavba = {
        nazev: s(d.nazev), adresa: s(d.adresa), cPovoleni: s(d.cPovoleni), iniciály: '',
      };
      const map = { nazev: 'name', sidlo: 'address', tel: 'phone' } as const;
      base.objednatel = firma(o(d.objednatel), map);
      base.zhotovitel = firma(o(d.zhotovitel), map);
      base.celkem = parseCzNum(d.smluvniCena);
      break;
    }
  }
  return base;
}

/** Nabídka a faktura mají `total` řádku (lze přepsat ručně) — počítá se z něj. */
function sumNabidkaFaktura(items: Obj[]): number {
  return items.reduce((a, it) => {
    const t = parseCzNum(it.total);
    return a + (t || parseCzNum(it.qty) * parseCzNum(it.price));
  }, 0);
}

let idSeq = 0;
const rid = (p: string) => `${p}-${Date.now()}-${++idSeq}`;

const firmaShort = (f: Firma) => ({
  nazev: f.nazev, sidlo: f.sidlo, ico: f.ico, dic: f.dic, tel: f.tel, email: f.email,
});

export interface TransferOptions {
  /**
   * Převod na soupis: `true` = postupná fakturace (množství prázdná, vyplní se
   * skutečně provedené), `false` = celé množství najednou.
   */
  postupne?: boolean;
  /** Pro ZP: součet „toto období" z předchozích ZP na stejném řetězci. */
  prevZp?: number;
  /** Pro ZP: pořadové číslo (počet předchozích + 1). */
  zpCislo?: string;
  smlouva?: { cislo: string; castka: string; typ?: 'objednavka' | 'sod' };
}

/** Sestaví částečný snímek dat cílového formuláře (pro jeho `applySnapshot`). */
export function buildTarget(
  srcType: ChainDocType,
  srcData: Obj,
  target: ChainDocType,
  opt: TransferOptions = {},
): Obj {
  const c = toCommon(srcType, srcData);
  const zdrojPopis = `${CHAIN_GENITIV[srcType]}${c.cislo ? ` č. ${c.cislo}` : ''}`;
  const sml = opt.smlouva;

  switch (target) {
    case 'objednavka':
      return {
        stavba: { nazev: c.stavba.nazev, adresa: c.stavba.adresa, cPovoleni: c.stavba.cPovoleni },
        obj: { nazev: c.objednatel.nazev, sidlo: c.objednatel.sidlo, ico: c.objednatel.ico, dic: c.objednatel.dic },
        dod: { nazev: c.zhotovitel.nazev, sidlo: c.zhotovitel.sidlo, ico: c.zhotovitel.ico, dic: c.zhotovitel.dic },
        polozky: c.polozky
          .filter((p) => !p.section)
          .map((p) => ({ id: rid('p'), popis: p.popis, mnozstvi: p.mnozstvi, jednotka: p.mj, cena: p.cena })),
        dphPct: c.dphPct,
      };

    case 'soupis':
      return {
        zakazka: c.zakazka,
        stavba: { ...c.stavba, sodCislo: sml?.cislo ?? '' },
        zhot: firmaShort(c.zhotovitel),
        obj: firmaShort(c.objednatel),
        rows: c.polozky.length
          ? c.polozky.map((p) =>
              p.section
                ? { id: rid('r'), type: 'section', title: p.popis }
                : {
                    id: rid('r'),
                    type: 'item',
                    title: p.popis,
                    unit: p.mj,
                    qty: opt.postupne ? '' : p.mnozstvi,
                    price: p.cena,
                  },
            )
          : undefined,
        dphPct: c.dphPct,
      };

    case 'zjistovaci':
      return {
        cislo: opt.zpCislo ?? '',
        zakazka: c.zakazka,
        obj: firmaShort(c.objednatel),
        zhot: firmaShort(c.zhotovitel),
        stavba: {
          ...c.stavba,
          sodCislo: sml?.cislo ?? '',
          sodCastka: sml?.castka ?? '',
        },
        prace: { prev: fmtCzNum(opt.prevZp ?? 0), curr: fmtCzNum(c.celkem) },
        dphPct: c.dphPct,
      };

    case 'faktura': {
      let items: Obj[];
      if (srcType === 'nabidka' || srcType === 'objednavka') {
        items = c.polozky
          .filter((p) => !p.section)
          .map((p) => ({
            id: rid('i'),
            desc: p.popis,
            qty: p.mnozstvi,
            unit: p.mj,
            price: p.cena,
            total: fmtCzNum(parseCzNum(p.mnozstvi) * parseCzNum(p.cena)),
          }));
      } else {
        const text =
          srcType === 'zjistovaci'
            ? `Provedené práce dle zjišťovacího protokolu${c.cislo ? ` č. ${c.cislo}` : ''}${c.zaObdobi ? ` za období ${c.zaObdobi}` : ''}`
            : srcType === 'soupis'
              ? `Provedené práce dle soupisu prací${c.cislo ? ` č. ${c.cislo}` : ''}`
              : `Dílo dle předávacího protokolu${c.cislo ? ` (${c.cislo})` : ''}`;
        items = [{
          id: rid('i'), desc: text, qty: '1', unit: 'kpl',
          price: fmtCzNum(c.celkem), total: fmtCzNum(c.celkem),
        }];
      }
      return {
        stavba: c.stavba,
        dod: { ...firmaShort(c.zhotovitel), ucet: c.zhotovitel.ucet },
        obj: firmaShort(c.objednatel),
        items,
        dphPct: c.dphPct,
        poznamky: [{
          id: rid('pzn'),
          text: `Fakturujeme Vám provedené práce dle ${zdrojPopis}${sml?.cislo ? ` a smlouvy/objednávky č. ${sml.cislo}` : ''}.`,
        }],
      };
    }

    case 'predavaci-dilo':
      return {
        nazev: c.stavba.nazev,
        adresa: c.stavba.adresa,
        cPovoleni: c.stavba.cPovoleni,
        cSmlouvy: sml?.cislo ?? '',
        cisloTyp: srcType === 'objednavka' || sml?.typ === 'objednavka' ? 'objednavka' : 'sod',
        objednatel: {
          name: c.objednatel.nazev, address: c.objednatel.sidlo, ico: c.objednatel.ico,
          zastoupeny: '', phone: c.objednatel.tel, email: c.objednatel.email,
        },
        zhotovitel: {
          name: c.zhotovitel.nazev, address: c.zhotovitel.sidlo, ico: c.zhotovitel.ico,
          ckait: '', stavbyvedouci: '', phone: c.zhotovitel.tel, email: c.zhotovitel.email,
        },
        smluvniCena: sml?.castka || (c.celkem ? fmtCzNum(c.celkem) : ''),
      };

    default:
      return {};
  }
}

/**
 * Smluvní základ, který se nese řetězcem: objednávka (číslo + cena), nabídka
 * (jen cena), u dalších dokladů převzetí od předka nebo z vlastních polí.
 */
export function smlouvaOf(
  srcType: ChainDocType,
  srcData: Obj,
  srcZdroj: ChainRef | null,
): { cislo: string; castka: string; typ?: 'objednavka' | 'sod' } | undefined {
  const c = toCommon(srcType, srcData);
  if (srcType === 'objednavka') return { cislo: c.cislo, castka: fmtCzNum(c.celkem), typ: 'objednavka' };
  if (srcZdroj?.smlouva) return srcZdroj.smlouva;
  if (srcType === 'nabidka') return { cislo: '', castka: fmtCzNum(c.celkem) };
  const st = o(srcData.stavba);
  if (srcType === 'soupis' && s(st.sodCislo)) return { cislo: s(st.sodCislo), castka: '' };
  if (srcType === 'zjistovaci' && (s(st.sodCislo) || s(st.sodCastka))) {
    return { cislo: s(st.sodCislo), castka: s(st.sodCastka) };
  }
  if (srcType === 'predavaci-dilo' && s(srcData.cSmlouvy)) {
    return { cislo: s(srcData.cSmlouvy), castka: s(srcData.smluvniCena) };
  }
  return undefined;
}

// ─── předávání mezi stránkami ───────────────────────────────────────────────

const TRANSFER_KEY = 'sd_doc_transfer';

interface TransferPayload {
  target: ChainDocType;
  data: Obj;
  ts: number;
}

export function stashTransfer(target: ChainDocType, data: Obj): void {
  const p: TransferPayload = { target, data, ts: Date.now() };
  sessionStorage.setItem(TRANSFER_KEY, JSON.stringify(p));
}

/** Vyzvedne (a smaže) převod určený pro daný typ. Starší než 10 min se zahodí. */
export function takeTransfer(target: string): Obj | null {
  let raw: string | null = null;
  try {
    raw = sessionStorage.getItem(TRANSFER_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;
  try {
    const p = JSON.parse(raw) as TransferPayload;
    if (p.target !== target) return null;
    sessionStorage.removeItem(TRANSFER_KEY);
    if (Date.now() - p.ts > 10 * 60_000) return null;
    return p.data;
  } catch {
    sessionStorage.removeItem(TRANSFER_KEY);
    return null;
  }
}

// ─── dotazy na archiv ───────────────────────────────────────────────────────

export function listDocs(docType: string): Promise<SavedDocument[]> {
  return api.get<SavedDocument[]>('/documents/', { query: { doc_type: docType } });
}

export function getDoc(id: number): Promise<SavedDocument> {
  return api.get<SavedDocument>(`/documents/${id}`);
}

/**
 * Předchozí ZP na stejném řetězci (stejný `koren`), bez `excludeId`.
 * Když řetězec není (ZP založené ručně), páruje se podle čísla zakázky.
 */
export async function previousZp(
  koren: number | null,
  zakazka: string,
  excludeId: number | null,
): Promise<{ count: number; sum: number }> {
  const all = await listDocs('zjistovaci');
  const zak = zakazka.trim();
  const same = all.filter((d) => {
    if (excludeId != null && d.id === excludeId) return false;
    const ref = readChainRef(d.data_json?.zdroj);
    if (koren != null && ref?.koren === koren) return true;
    return koren == null && !!zak && s(d.data_json?.zakazka).trim() === zak;
  });
  const sum = same.reduce((a, d) => a + parseCzNum(o(d.data_json?.prace).curr), 0);
  return { count: same.length, sum };
}

/**
 * Doklady, které patří k faktuře: předci po řetězci `zdroj` + předávací
 * protokoly díla na stejném kořeni (vznikají bokem ze soupisu/objednávky).
 */
export async function invoiceRelatedDocs(zdroj: ChainRef | null): Promise<SavedDocument[]> {
  if (!zdroj) return [];
  const out: SavedDocument[] = [];
  const seen = new Set<number>();
  let ref: ChainRef | null = zdroj;
  // Pojistka proti cyklu / nekonečné smyčce.
  for (let i = 0; ref && i < 10 && !seen.has(ref.id); i++) {
    seen.add(ref.id);
    try {
      const d = await getDoc(ref.id);
      out.push(d);
      ref = readChainRef(d.data_json?.zdroj);
    } catch {
      break; // předek smazaný — řetězec končí
    }
  }
  try {
    const pp = await listDocs('predavaci-dilo');
    for (const d of pp) {
      const r = readChainRef(d.data_json?.zdroj);
      if (!seen.has(d.id) && r && (r.koren === zdroj.koren || seen.has(r.id))) {
        seen.add(d.id);
        out.push(d);
      }
    }
  } catch {
    /* bez předávacích protokolů */
  }
  return out;
}
